import React from 'react';
import { PlacedLabel, PlacedBlock, SocketConnection, TextAnnotation } from '@/app/types/cad';
import { Label } from '@/app/components/ui/label';
import { ScrollArea } from '@/app/components/ui/scroll-area';
import { Button } from '@/app/components/ui/button';
import { Edit2, RotateCw, AlertTriangle } from 'lucide-react';

interface LabelPropertiesProps {
  labels: PlacedLabel[];
  allBlocks: PlacedBlock[];
  allConnections: SocketConnection[];
  allTextAnnotations: TextAnnotation[];
  onLabelEdit?: (labelId: string) => void;
  onLabelReassign?: (labelId: string) => void; // NEW: Callback for reassigning orphaned labels
}

export function LabelProperties({
  labels,
  allBlocks,
  allConnections,
  allTextAnnotations,
  onLabelEdit,
  onLabelReassign,
}: LabelPropertiesProps) {
  const singleLabel = labels.length === 1 ? labels[0] : null;

  if (!singleLabel) {
    return (
      <ScrollArea className="h-full">
        <div className="p-4">
          <h3 className="font-medium mb-2">Mehrfachauswahl</h3>
          <p className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
            {labels.length} Labels ausgewählt
          </p>
        </div>
      </ScrollArea>
    );
  }

  // Find the target element AND check if it's orphaned
  let targetName = 'Unbekannt';
  let targetType = singleLabel.targetElementType;
  let targetDetails: string | null = null;
  let isOrphaned = false; // NEW: Track if target element no longer exists
  
  if (targetType === 'block') {
    const block = allBlocks.find(b => b.id === singleLabel.targetElementId);
    if (block) {
      targetName = block.blockData.name;
      targetDetails = `ID: ${block.id.slice(0, 12)}...`;
    } else {
      isOrphaned = true;
      targetName = 'Nicht gefunden';
      targetDetails = 'Block wurde gelöscht';
    }
  } else if (targetType === 'connection') {
    const connection = allConnections.find(c => c.id === singleLabel.targetElementId);
    if (connection) {
      // Find connected blocks
      const fromBlock = allBlocks.find(b => b.id === connection.fromBlockId);
      const toBlock = allBlocks.find(b => b.id === connection.toBlockId);
      if (fromBlock && toBlock) {
        targetName = `${fromBlock.blockData.name} → ${toBlock.blockData.name}`;
        targetDetails = `${connection.fromSocket} → ${connection.toSocket}`;
      } else {
        targetName = `Verbindung`;
        targetDetails = connection.id.slice(0, 12) + '...';
      }
    } else {
      isOrphaned = true;
      targetName = 'Nicht gefunden';
      targetDetails = 'Verbindung wurde gelöscht';
    }
  } else if (targetType === 'text-annotation') {
    const text = allTextAnnotations.find(t => t.id === singleLabel.targetElementId);
    if (text) {
      targetName = text.content.substring(0, 30) + (text.content.length > 30 ? '...' : '');
      targetDetails = `Größe: ${text.fontSize}px`;
    } else {
      isOrphaned = true;
      targetName = 'Nicht gefunden';
      targetDetails = 'Text-Annotation wurde gelöscht';
    }
  }

  const typeDisplayName = targetType === 'block' ? 'Block' : 
                          targetType === 'connection' ? 'Verbindung' : 
                          targetType === 'text-annotation' ? 'Text-Annotation' : 'Unbekannt';

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div>
          <h3 className="font-medium mb-3">Label-Eigenschaften</h3>
          
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--md-on-surface-variant)' }}>
                Verbunden mit
              </Label>
              <div className="mt-1.5 p-3 rounded" style={{ backgroundColor: 'var(--md-surface-container)' }}>
                <div className="text-sm font-medium">{targetName}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                  Typ: {typeDisplayName}
                </div>
                {targetDetails && (
                  <div className="text-xs mt-0.5" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {targetDetails}
                  </div>
                )}
                {isOrphaned && (
                  <div className="flex items-center gap-1 mt-2 text-xs" style={{ color: 'var(--md-error)' }}>
                    <AlertTriangle className="w-3 h-3" />
                    <span>Ziel-Element existiert nicht mehr</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--md-on-surface-variant)' }}>
                Position
              </Label>
              <div className="text-sm mt-1">
                u: {singleLabel.position.u.toFixed(2)}, v: {singleLabel.position.v.toFixed(2)}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--md-on-surface-variant)' }}>
                Anzeigewerte
              </Label>
              <div className="mt-1.5 p-3 rounded text-sm space-y-1" style={{ backgroundColor: 'var(--md-surface-container)' }}>
                {singleLabel.resolvedData.map((row, rowIdx) => (
                  <div key={rowIdx} className="flex gap-3">
                    {row.map((cell, cellIdx) => (
                      <span key={cellIdx} className="font-mono text-xs">{cell || '—'}</span>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {onLabelEdit && (
              <div>
                <Button
                  className="mt-4"
                  onClick={() => onLabelEdit(singleLabel.id)}
                >
                  <Edit2 className="mr-2" />
                  Bearbeiten
                </Button>
              </div>
            )}

            {onLabelReassign && (
              <div>
                <Button
                  variant={isOrphaned ? 'default' : 'outline'}
                  className="mt-4"
                  onClick={() => onLabelReassign(singleLabel.id)}
                >
                  <RotateCw className="mr-2" />
                  Neu zuweisen
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}